import { ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { apps } from '../config/apps.config'

export default function Dashboard() {
  return (
    <div className="py-24 relative min-h-screen flex flex-col">
      <Helmet>
        <title>Dashboard - Headstate Studio | Launch Your Creative Tools</title>
        <meta name="description" content="Access all Headstate Studio creative tools from one dashboard. Launch the free online DAW, video editor, image editor, and 3D editor instantly in your browser." />
        <link rel="canonical" href="https://headstate.studio/dashboard" />
      </Helmet>
      
      {/* Background Ambience */}
      <div className="fixed inset-0 bg-grid-pattern opacity-20 pointer-events-none z-0"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full flex-grow">
        <div className="mb-16">
          <h1 className="text-5xl font-display font-bold text-gloss mb-3 tracking-tighter">DASHBOARD</h1>
          <p className="text-hs-text/80 font-light text-lg tracking-wide">Launch and manage your premium creative tools.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {apps.map((app) => {
            const Icon = app.icon
            return (
              <div
                key={app.id}
                className="neu-panel relative group overflow-hidden transition-all duration-300 hover:shadow-glossy hover:scale-[1.01]"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="p-8 relative z-10">
                  <div className="flex items-start justify-between mb-10">
                    <div className="flex items-center space-x-6">
                      <div
                        className="w-16 h-16 neu-panel-inset rounded-2xl flex items-center justify-center border border-black/40 shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]"
                      >
                         <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none rounded-2xl"></div>
                        <Icon size={32} style={{ color: app.color }} className="drop-shadow-[0_0_12px_rgba(255,255,255,0.2)]" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-display font-bold text-white tracking-tight drop-shadow-sm">{app.name}</h3>
                        <p className="text-sm text-hs-text/80 mt-1 font-light">{app.description}</p>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-3 py-1.5 shadow-neu-in-deep bg-black/50 rounded-md text-[10px] font-mono font-bold border border-white/5 text-hs-text/70 tracking-widest uppercase">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-2 shadow-[0_0_5px_#22c55e]"></span>
                      {app.status}
                    </span>
                  </div>

                  <div className="flex items-center space-x-4">
                    <Link
                      to={`/app/${app.id}`}
                      className="flex-1 relative neu-button overflow-hidden group/btn px-4 py-4"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-hs-core to-[#1A5CB8] opacity-80 group-hover/btn:opacity-100 transition-opacity"></div>
                      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.4)_0%,rgba(255,255,255,0)_50%)] pointer-events-none"></div>
                      <span className="relative z-10 flex items-center justify-center font-mono font-bold text-xs text-white tracking-widest uppercase drop-shadow-md">
                        Launch Studio
                      </span>
                    </Link>
                    <a
                      href={`https://${app.subdomain}.headstate.studio`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="neu-button px-5 py-4 text-hs-text hover:text-white transition-colors flex items-center justify-center group/link"
                    >
                       <ExternalLink size={20} className="group-hover/link:scale-110 transition-transform drop-shadow-md" />
                    </a>
                  </div>
                </div>
                {/* Glow behind the card */}
                 <div 
                  className="absolute -inset-20 opacity-0 group-hover:opacity-20 transition-opacity pointer-events-none duration-700 blur-[80px] z-0"
                  style={{ background: `radial-gradient(circle at center, ${app.color} 0%, transparent 50%)` }}
                 />
              </div>
            )
          })}
        </div>

        {/* Recent Projects Section */}
        <div className="mt-20">
          <h2 className="text-2xl font-display font-bold text-white mb-8 tracking-tight drop-shadow-sm">RECENT PROJECTS</h2>
          <div className="neu-panel-inset rounded-2xl p-16 text-center border border-black/50 relative overflow-hidden">
             <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 pointer-events-none"></div>
            <p className="relative z-10 text-hs-text/60 font-mono text-sm uppercase tracking-widest flex items-center justify-center">
              <span className="mr-3 text-hs-text/40">{"//"}</span> No recent projects found. Start creating.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
